import React, { useState } from 'react';
import { apiService } from '../services/api';

interface BoletoAPITesterProps {
  invoiceId: string;
}

const BoletoAPITester: React.FC<BoletoAPITesterProps> = ({ invoiceId }) => {
  const [results, setResults] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);

  const addResult = (test: string, success: boolean, data: any, error?: string) => {
    const result = {
      test,
      success,
      data,
      error,
      timestamp: new Date().toLocaleString() // Use locale-specific time string
    };
    setResults(prev => [result, ...prev]);
    console.log(`🧪 ${test}:`, result);
  };

  const testAPI = async () => {
    setLoading(true);
    setResults([]);

    try {
      // Test 1: Check eligibility
      console.log('🧪 Testing boleto eligibility check...');
      try {
        const eligibilityResponse = await apiService.axiosInstance.get(`/api/v1/boleto/check/${invoiceId}`);
        addResult('Eligibility Check', true, eligibilityResponse.data);
      } catch (error: any) {
        addResult('Eligibility Check', false, null, error.response?.data?.detail || error.message);
      }

      // Test 2: Get invoice details
      console.log('🧪 Testing invoice details...');
      try {
        const invoiceResponse = await apiService.axiosInstance.get(`/api/v1/invoices/${invoiceId}`);
        addResult('Invoice Details', true, {
          currency: invoiceResponse.data.currency,
          status: invoiceResponse.data.invoice_status,
          customer_id: invoiceResponse.data.customer_id
        });
      } catch (error: any) {
        addResult('Invoice Details', false, null, error.response?.data?.detail || error.message);
      }

      // Test 3: Try to create boleto (expect confirmation or success)
      console.log('🧪 Testing boleto creation...');
      try {
        const createResponse = await apiService.axiosInstance.post(`/api/v1/boleto/create/${invoiceId}`, {
          notification_url: 'https://your-domain.com/api/v1/boleto/webhook/epag',
          force_create: false
        });
        addResult('Boleto Creation', true, createResponse.data);
      } catch (error: any) {
        addResult('Boleto Creation', false, error.response?.data, error.response?.data?.detail || error.message);
      }

      // Test 4: Check authentication
      console.log('🧪 Testing authentication...');
      try {
        const token = localStorage.getItem('access_token') || localStorage.getItem('token');
        addResult('Authentication', !!token, { hasToken: !!token, tokenLength: token?.length || 0 });
      } catch (error: any) {
        addResult('Authentication', false, null, error.message);
      }

    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{ 
      border: '2px solid #007bff', 
      padding: '15px', 
      margin: '10px 0', 
      borderRadius: '5px',
      backgroundColor: '#f0f8ff'
    }}>
      <h4>🧪 Boleto API Tester</h4>
      
      <button 
        onClick={testAPI} 
        disabled={loading}
        className="btn btn-primary"
        style={{ marginBottom: '15px' }}
      >
        {loading ? 'Running Tests...' : 'Run API Tests'}
      </button>
      
      {results.length > 0 && (
        <div>
          <h5>Test Results:</h5>
          {results.map((result, index) => (
            <div 
              key={index}
              style={{ 
                marginBottom: '10px', 
                padding: '10px', 
                backgroundColor: result.success ? '#d4edda' : '#f8d7da',
                border: `1px solid ${result.success ? '#c3e6cb' : '#f5c6cb'}`,
                borderRadius: '3px'
              }}
            >
              <strong>{result.success ? '✅' : '❌'} {result.test}</strong>
              {result.error && (
                <div style={{ color: '#721c24', fontSize: '12px', marginTop: '5px' }}>
                  Error: {result.error}
                </div>
              )}
              {result.data && (
                <details style={{ marginTop: '5px' }}>
                  <summary style={{ cursor: 'pointer', fontSize: '12px' }}>View Data</summary>
                  <pre style={{ 
                    fontSize: '10px', 
                    backgroundColor: '#fff', 
                    padding: '5px', 
                    marginTop: '5px',
                    overflow: 'auto',
                    maxHeight: '200px'
                  }}>
                    {JSON.stringify(result.data, null, 2)}
                  </pre>
                </details>
              )}
              <div style={{ fontSize: '10px', color: '#666', marginTop: '5px' }}>
                {result.timestamp}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default BoletoAPITester;